import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../client';
import { failureOf } from '@/lib/result';
import { canRecord, startRecording } from '@/lib/wav-recorder';
import type { Recording } from '@/lib/wav-recorder';
import { useToast } from '../shell/toast';

export type VoiceState = 'idle' | 'listening' | 'transcribing';

/**
 * The mic button: record, then on the second press encode a 16 kHz WAV, send
 * it to /chat/transcribe and hand the text to the composer. `supported` is
 * false where the browser cannot record, and the button is not shown at all.
 */
export function useVoice(onText: (text: string) => void): {
  supported: boolean;
  state: VoiceState;
  toggle: () => void;
} {
  const toast = useToast();
  const [supported] = useState(canRecord);
  const [state, setState] = useState<VoiceState>('idle');
  const recording = useRef<Recording | null>(null);
  const deliver = useRef(onText);
  useEffect(() => {
    deliver.current = onText;
  }, [onText]);
  useEffect(() => () => recording.current?.cancel(), []);

  const finish = useCallback(async (): Promise<void> => {
    const current = recording.current;
    recording.current = null;
    if (current === null) return;
    setState('transcribing');
    try {
      const wav = await current.stop();
      const file = new File([wav], 'question.wav', { type: 'audio/wav' });
      const r = await api.chat.transcribe({ body: { file } });
      if (r.status === 200) {
        if (r.body.text.trim() === '') toast('Nothing was heard — try again closer to the mic');
        else deliver.current(r.body.text.trim());
      } else if (r.status === 503) {
        toast('Voice needs AI to be configured');
      } else {
        toast(failureOf(r).detail ?? `Could not transcribe (${r.status})`);
      }
    } catch {
      toast('Could not transcribe: the API did not answer');
    } finally {
      setState('idle');
    }
  }, [toast]);

  const toggle = useCallback(() => {
    if (state === 'transcribing') return;
    if (state === 'listening') {
      void finish();
      return;
    }
    setState('listening');
    startRecording(() => void finish())
      .then((r) => {
        recording.current = r;
      })
      .catch(() => {
        setState('idle');
        toast('The microphone is blocked or missing');
      });
  }, [finish, state, toast]);

  return { supported, state, toggle };
}
